import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { ReturnPathTracker } from "@/lib/authRedirect";
import { FocusTarget } from "@/lib/focusTarget";
import { InboxReturnBar } from "@/components/inbox/InboxReturnBar";
import { HelmetProvider } from 'react-helmet-async';
import { Navigation } from "@/components/ui/navigation";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Footer } from "@/components/ui/footer";
import { lazy, Suspense, useEffect } from "react";
import { EditModeProvider } from "@/components/EditModeProvider";
import { ShoppingCartProvider } from "@/contexts/ShoppingCartContext";
import { initializeResourceHints } from "@/utils/resourceHints";
import { useUTMTracking } from "@/hooks/useUTMTracking";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { EventChatbot } from "@/components/chat/EventChatbot";
import { useScrollRestoration } from "@/hooks/useScrollRestoration";
import { ExitIntentPopup } from "@/components/marketing/ExitIntentPopup";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { SpeakerVolunteerOnboarding } from "@/components/organizer/SpeakerVolunteerOnboarding";
import { BugReportButton } from "@/components/BugReportButton";

// Core pages - Home loaded immediately as entry point, others lazy loaded
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Lazy load these core pages to reduce main bundle size
const Events = lazy(() => import("./pages/Events"));
const OurEvents = lazy(() => import("./pages/OurEvents"));
const NomadFestsWorldwide = lazy(() => import("./pages/NomadFestsWorldwide"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MarketplaceDetail = lazy(() => import("./pages/MarketplaceDetail"));
const MarketplaceEditor = lazy(() => import("./pages/MarketplaceEditor"));
const MyMarketplace = lazy(() => import("./pages/MyMarketplace"));
const Auth = lazy(() => import("./pages/Auth"));

// Lazy load EventDetail (heavy component with lots of sub-components)
const EventDetail = lazy(() => import("./pages/EventDetail"));

// Lazy load heavy admin pages and less frequently accessed pages
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const AdminReminderSettings = lazy(() => import("./pages/AdminReminderSettings"));
const AdminBankSettings = lazy(() => import("./pages/AdminBankSettings"));
const AdminEmailSettings = lazy(() => import("./pages/AdminEmailSettings"));
const AdminBundles = lazy(() => import("./pages/AdminBundles"));
const AdminInventory = lazy(() => import("./pages/AdminInventory"));
const AdminImageCompression = lazy(() => import("./pages/AdminImageCompression"));
const AdminAIImageStudio = lazy(() => import("./pages/AdminAIImageStudio"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AccessRedeem = lazy(() => import("./pages/AccessRedeem"));
const AdminEventEditor = lazy(() => import("./pages/AdminEventEditor"));
const AdminLoyalty = lazy(() => import("./pages/AdminLoyalty"));
const AdminProductLibrary = lazy(() => import("./pages/AdminProductLibrary"));
const AdminMangobedsBookings = lazy(() => import("./pages/AdminMangobedsBookings"));
const ImportEvent = lazy(() => import("./pages/ImportEvent"));
const AdminExternalEvents = lazy(() => import("./pages/AdminExternalEvents"));
const AdminEventScraper = lazy(() => import("./pages/AdminEventScraper"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminSocialSettings = lazy(() => import("./pages/AdminSocialSettings"));
const AdminAISettings = lazy(() => import("./pages/AdminAISettings"));
const BlockRegenerationStudio = lazy(() => import("./pages/BlockRegenerationStudio"));
const AdminCompetitions = lazy(() => import("./pages/AdminCompetitions"));
const AdminCommunityRewards = lazy(() => import("./pages/AdminCommunityRewards"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const Messages = lazy(() => import("./pages/Messages"));
const CommunityMembers = lazy(() => import("./pages/CommunityMembers"));
const ProfileDetail = lazy(() => import("./pages/ProfileDetail"));
const AdminEmbedManager = lazy(() => import("./pages/AdminEmbedManager"));
const AdminSeoLandingPages = lazy(() => import("./pages/AdminSeoLandingPages"));
const AdminCreateAccommodation = lazy(() => import("./pages/AdminCreateAccommodation"));
const AccommodationHub = lazy(() => import("./pages/AccommodationHub"));

// Lazy load AI/builder tools (heavy and rarely used)
const BlockBuilder = lazy(() => import("./pages/BlockBuilder"));
const AIBlockBuilder = lazy(() => import("./pages/AIBlockBuilder"));
const AIPageGenerator = lazy(() => import("./pages/AIPageGenerator"));
const AIEventWizard = lazy(() => import("./pages/AIEventWizard"));
const JSONEventCreator = lazy(() => import("./pages/JSONEventCreator"));
const BlockMigration = lazy(() => import("./pages/BlockMigration"));
const BlockExportImport = lazy(() => import("./pages/BlockExportImport"));
const CreateAlpineEvent = lazy(() => import("./pages/CreateAlpineEvent"));

// Lazy load other pages
const GooglePhotosCallback = lazy(() => import("./pages/GooglePhotosCallback"));
const LinkedInCallback = lazy(() => import("./pages/LinkedInCallback"));
const InvoiceThankYou = lazy(() => import("./pages/InvoiceThankYou"));
const GuestOrder = lazy(() => import("./pages/GuestOrder"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const CustomerProfile = lazy(() => import("./pages/CustomerProfile"));
const LoyaltyRewards = lazy(() => import("./pages/LoyaltyRewards"));
const MyAffiliate = lazy(() => import("./pages/MyAffiliate"));
const EventShopPage = lazy(() => import("./pages/EventShop"));
const BookRoom = lazy(() => import("./pages/BookRoom"));
const SpeakerProfile = lazy(() => import("./pages/SpeakerProfile"));
const EventImageManagement = lazy(() => import("./pages/EventImageManagement"));
const EventVideoManagement = lazy(() => import("./pages/EventVideoManagement"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminInbox = lazy(() => import("./pages/AdminInbox"));
const HubInbox = lazy(() => import("./pages/HubInbox"));
const MailDesk = lazy(() => import("./pages/MailDesk"));
const AdminBugReports = lazy(() => import("./pages/AdminBugReports"));
const AdminHealth = lazy(() => import("./pages/AdminHealth"));
const MyBugReports = lazy(() => import("./pages/MyBugReports"));
const AdminActivityFeed = lazy(() => import("./pages/AdminActivityFeed"));
const AwardsLanding = lazy(() => import("./pages/AwardsLanding"));
const AwardDetail = lazy(() => import("./pages/AwardDetail"));
const Partners = lazy(() => import("./pages/Partners"));
const Community = lazy(() => import("./pages/Community"));
const CommunityHub = lazy(() => import("./pages/CommunityHub"));
const CommunityLocal = lazy(() => import("./pages/CommunityLocal"));
const CommunityGlobal = lazy(() => import("./pages/CommunityGlobal"));
const MastermindDetail = lazy(() => import("./pages/MastermindDetail"));
const FirstSwissNomadFest = lazy(() => import("./pages/FirstSwissNomadFest"));
const Directory = lazy(() => import("./pages/Directory"));
const Contact = lazy(() => import("./pages/Contact"));
const SubmitEvent = lazy(() => import("./pages/SubmitEvent"));
const MyEvents = lazy(() => import("./pages/MyEvents"));
const OrganizerDashboard = lazy(() => import("./pages/OrganizerDashboard"));
const BlogEditor = lazy(() => import("./components/BlogEditor").then(m => ({ default: m.BlogEditor })));
const Test = lazy(() => import("./pages/Test"));
const UploadHeroImage = lazy(() => import("./pages/UploadHeroImage"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const AwardsTerms = lazy(() => import("./pages/AwardsTerms"));
const PartnerTerms = lazy(() => import("./pages/PartnerTerms"));
const CoOrganizerTerms = lazy(() => import("./pages/CoOrganizerTerms"));
const ShopSuccess = lazy(() => import("./pages/ShopSuccess").then(m => ({ default: m.ShopSuccess })));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const BecomeAffiliate = lazy(() => import("./pages/BecomeAffiliate"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));

const ListYourEvent = lazy(() => import("./pages/ListYourEvent"));

// Print pages (standalone, no navigation/footer)
const PrintOverview = lazy(() => import("./pages/print/PrintOverview"));
const PrintSwissNomadFest = lazy(() => import("./pages/print/PrintSwissNomadFest"));
const PrintFireWeekend = lazy(() => import("./pages/print/PrintFireWeekend"));
const PrintLongevityFest = lazy(() => import("./pages/print/PrintLongevityFest"));
const PrintSnomadFest = lazy(() => import("./pages/print/PrintSnomadFest"));
const PrintListYourEvent = lazy(() => import("./pages/print/PrintListYourEvent"));

// Embed pages (lightweight, no navigation)
const EmbedEvent = lazy(() => import("./pages/embed/EmbedEvent"));
const EmbedProduct = lazy(() => import("./pages/embed/EmbedProduct"));
const EmbedEventList = lazy(() => import("./pages/embed/EmbedEventList"));
const EmbedAwards = lazy(() => import("./pages/embed/EmbedAwards"));
const EmbedAwardsApply = lazy(() => import("./pages/embed/EmbedAwardsApply"));

// SEO Landing pages
const EventLanding = lazy(() => import("./pages/landing/EventLanding"));
const WhatIsNomadFest = lazy(() => import("./pages/WhatIsNomadFest"));

// Preload critical routes on idle
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    // Preload EventDetail - the most common next navigation from home page
    import("./pages/EventDetail");
  });
}

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
      <p className="mt-4 text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - keep data fresh longer
      gcTime: 30 * 60 * 1000, // 30 minutes - cache data longer
      retry: 1,
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      refetchOnMount: false, // Use cached data on mount if available
      refetchOnReconnect: false, // Don't refetch on reconnect
    },
    mutations: {
      retry: 1,
    },
  },
});

const AppContent = () => {
  useUTMTracking();
  useAffiliateTracking();
  useScrollRestoration();
  
  // Initialize resource hints on mount
  useEffect(() => {
    initializeResourceHints();
    
    // Service worker registration lives in src/main.tsx (single registrar).
  }, []);

  return null;
};

const App = () => {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ShoppingCartProvider>
          <EditModeProvider isAdmin={false} defaultEditMode={false}>
            <BrowserRouter>
              <ReturnPathTracker />
              <FocusTarget />
              <InboxReturnBar />
              <AppContent />
              <Routes>
                {/* Print routes - standalone A3 sheets, no navigation/footer */}
                <Route path="/print/*" element={
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="overview" element={<PrintOverview />} />
                      <Route path="swiss-nomad-fest" element={<PrintSwissNomadFest />} />
                      <Route path="fire-weekend" element={<PrintFireWeekend />} />
                      <Route path="longevity-fest" element={<PrintLongevityFest />} />
                      <Route path="snomad-fest" element={<PrintSnomadFest />} />
                      <Route path="list-your-event" element={<PrintListYourEvent />} />
                    </Routes>
                  </Suspense>
                } />

                {/* Embed routes - completely standalone, no navigation/footer */}
                <Route path="/embed/*" element={
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="event/:id" element={<EmbedEvent />} />
                      <Route path="product/:id" element={<EmbedProduct />} />
                      <Route path="events" element={<EmbedEventList />} />
                      <Route path="awards" element={<EmbedAwards />} />
                      <Route path="awards/:slug" element={<EmbedAwards />} />
                      <Route path="awards-apply" element={<EmbedAwardsApply />} />
                    </Routes>
                  </Suspense>
                } />
                
                {/* Postfach on the computer (3 columns) - no site navigation */}
                <Route path="/mail" element={
                  <Suspense fallback={<PageLoader />}>
                    <MailDesk />
                  </Suspense>
                } />

                {/* Notification inbox (installable app) - no site navigation */}
                <Route path="/inbox" element={
                  <Suspense fallback={<PageLoader />}>
                    <HubInbox />
                  </Suspense>
                } />

                {/* All other routes with navigation and footer */}
                <Route path="/*" element={
                  <>
                    <Navigation />
                    <Suspense fallback={<PageLoader />}>
                      <div>
                        <AdminLayout>
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/landing/:slug" element={<EventLanding />} />
                          <Route path="/what-is-nomad-fest" element={<WhatIsNomadFest />} />
                          <Route path="/events" element={<Events />} />
                          <Route path="/our-events" element={<OurEvents />} />
                          <Route path="/nomad-fests-worldwide" element={<NomadFestsWorldwide />} />
                          <Route path="/marketplace" element={<Marketplace />} />
                          <Route path="/marketplace/new" element={<MarketplaceEditor />} />
                          <Route path="/marketplace/me" element={<MyMarketplace />} />
                          <Route path="/marketplace/edit/:id" element={<MarketplaceEditor />} />
                          <Route path="/marketplace/:id" element={<MarketplaceDetail />} />
                          {/* Back-compat with the earlier talent-only routes */}
                          <Route path="/talent" element={<Navigate to="/marketplace?kind=talent" replace />} />
                          <Route path="/talent/me" element={<Navigate to="/marketplace/me" replace />} />
                          <Route path="/talent/:id" element={<MarketplaceDetail />} />
                          <Route path="/events/:category/:id" element={<EventDetail />} />
                          <Route path="/events/:category/:id/shop" element={<EventShopPage />} />
                          <Route path="/events/:category/:id/images" element={<EventImageManagement />} />
                          <Route path="/events/:category/:id/videos" element={<EventVideoManagement />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/blog/:slug" element={<BlogPost />} />
                          <Route path="/blog/create" element={<BlogEditor />} />
                          <Route path="/blog/edit/:id" element={<BlogEditor />} />
                          <Route path="/community" element={<Community />} />
                          <Route path="/community/local" element={<CommunityLocal />} />
                          <Route path="/community/global" element={<CommunityGlobal />} />
                          <Route path="/community/global/masterminds/:id" element={<MastermindDetail />} />
                          {/* Legacy redirects */}
                          <Route path="/community-hub" element={<CommunityLocal />} />
                          <Route path="/community-hub/masterminds/:id" element={<MastermindDetail />} />
                          <Route path="/retrospective/1st-swiss-nomad-fest" element={<FirstSwissNomadFest />} />
                          <Route path="/directory" element={<Directory />} />
                          <Route path="/awards" element={<AwardsLanding />} />
                          <Route path="/awards/:slug" element={<AwardDetail />} />
                          <Route path="/partners" element={<Partners />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/jobs" element={<Jobs />} />
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/terms" element={<Terms />} />
                          <Route path="/partner-terms" element={<PartnerTerms />} />
                          <Route path="/co-organizer-terms" element={<CoOrganizerTerms />} />
                          <Route path="/awards-terms" element={<AwardsTerms />} />
                          <Route path="/submit-event" element={<SubmitEvent />} />
                          <Route path="/list-your-event" element={<ListYourEvent />} />
                          <Route path="/add-event" element={<ListYourEvent />} />
                          <Route path="/my-events" element={<MyEvents />} />
                          <Route path="/organizer-dashboard" element={<OrganizerDashboard />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/auth" element={<Auth />} />
                          <Route path="/auth/google-photos-callback" element={<GooglePhotosCallback />} />
                          <Route path="/access/redeem/:token" element={<AccessRedeem />} />
                          <Route path="/admin" element={<AdminDashboard />} />
                          <Route path="/admin/inbox" element={<AdminInbox />} />
                          <Route path="/admin/bug-reports" element={<AdminBugReports />} />
                          <Route path="/admin/health" element={<AdminHealth />} />
                          <Route path="/my-bug-reports" element={<MyBugReports />} />
                          <Route path="/admin/live" element={<AdminActivityFeed />} />
                          <Route path="/admin/events/:eventId" element={<AdminEventEditor />} />
                          <Route path="/admin/products" element={<AdminProducts />} />
                          <Route path="/admin/orders" element={<AdminOrders />} />
                          <Route path="/admin/invoices" element={<AdminInvoices />} />
                          <Route path="/admin/reminder-settings" element={<AdminReminderSettings />} />
                          <Route path="/admin/bank-settings" element={<AdminBankSettings />} />
                          <Route path="/admin/email-settings" element={<AdminEmailSettings />} />
                          <Route path="/admin/bundles" element={<AdminBundles />} />
                          <Route path="/admin/inventory" element={<AdminInventory />} />
                          <Route path="/admin/image-compression" element={<AdminImageCompression />} />
                          <Route path="/admin/ai-image-studio" element={<AdminAIImageStudio />} />
                          <Route path="/admin/loyalty" element={<AdminLoyalty />} />
                          <Route path="/admin/community-rewards" element={<AdminCommunityRewards />} />
                          <Route path="/admin/leads" element={<AdminLeads />} />
                          <Route path="/messages" element={<AdminLeads />} />
                          <Route path="/admin/product-library" element={<AdminProductLibrary />} />
                          <Route path="/admin/mangobeds-bookings" element={<AdminMangobedsBookings />} />
                          <Route path="/admin/accommodation-hub" element={<AccommodationHub />} />
                          <Route path="/admin/import-event" element={<ImportEvent />} />
                          <Route path="/admin/external-events" element={<AdminExternalEvents />} />
                          <Route path="/admin/event-scraper" element={<AdminEventScraper />} />
                          <Route path="/admin/tasks" element={<AdminTasks />} />
                          <Route path="/admin/json-event-creator" element={<JSONEventCreator />} />
                          <Route path="/admin/marketing" element={<AdminMarketing />} />
                          <Route path="/admin/social-settings" element={<AdminSocialSettings />} />
                          <Route path="/admin/ai-settings" element={<AdminAISettings />} />
                          <Route path="/admin/linkedin-callback" element={<LinkedInCallback />} />
                          <Route path="/admin/block-regeneration" element={<BlockRegenerationStudio />} />
                          <Route path="/my-orders" element={<MyOrders />} />
                          <Route path="/profile" element={<CustomerProfile />} />
                          <Route path="/messages" element={<Messages />} />
                          <Route path="/community/members" element={<CommunityMembers />} />
                          <Route path="/community/members/:id" element={<ProfileDetail />} />
                          <Route path="/rewards" element={<LoyaltyRewards />} />
                          <Route path="/my-affiliate" element={<MyAffiliate />} />
                          <Route path="/admin/block-builder" element={<BlockBuilder />} />
                          <Route path="/admin/ai-block-builder" element={<AIBlockBuilder />} />
                          <Route path="/admin/ai-page-generator" element={<AIPageGenerator />} />
                          <Route path="/admin/ai-event-wizard" element={<AIEventWizard />} />
                          <Route path="/admin/block-migration" element={<BlockMigration />} />
                          <Route path="/admin/block-export-import" element={<BlockExportImport />} />
                          <Route path="/admin/create-alpine-event" element={<CreateAlpineEvent />} />
                          <Route path="/admin/embed-manager" element={<AdminEmbedManager />} />
                          <Route path="/admin/seo-landing-pages" element={<AdminSeoLandingPages />} />
                          <Route path="/admin/competitions" element={<AdminCompetitions />} />
                          <Route path="/admin/create-accommodation" element={<AdminCreateAccommodation />} />
                          <Route path="/shop-success" element={<ShopSuccess />} />
                          <Route path="/book-room" element={<BookRoom />} />
                          <Route path="/speaker/profile" element={<SpeakerProfile />} />
                          <Route path="/invoice-thank-you" element={<InvoiceThankYou />} />
                          <Route path="/guest-order" element={<GuestOrder />} />
                          <Route path="/accept-invite" element={<AcceptInvite />} />
                          <Route path="/become-affiliate" element={<BecomeAffiliate />} />
                          <Route path="/affiliate-dashboard" element={<AffiliateDashboard />} />
                          <Route path="/test" element={<Test />} />
                          <Route path="/upload-hero" element={<UploadHeroImage />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </AdminLayout>
                      </div>
                    </Suspense>
                    <Footer />
                    <EventChatbot />
                    <ExitIntentPopup />
                    <LocalBusinessSchema />
                    <SpeakerVolunteerOnboarding />
                    <BugReportButton />
                  </>
                } />
              </Routes>
            </BrowserRouter>
          </EditModeProvider>
        </ShoppingCartProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
